import {
    geocodeErrorMessages,
    periodicUsageTypesForOil,
    periodicUsageTypesForPropane,
} from 'lib/constants';
import { DeviceFuelType, Geometry } from 'types';
import _ from 'lodash';

const SIG_VALUES = [
    'BUTTON_PRESS',
    'TEST',
    'SLEEP',
    'EEPROM_INI',
    'COV_TRIPPED',
    'TCOV_TRIPPED',
    'THRESH1_TRIPPED',
    'THRESH1_LATCHED',
    'SEND_NOW',
    'BAD_REPORT_TIME',
    'COMM_FAIL',
    'THRESH2_TRIPPED',
    'THRESH2_LATCHED',
    'NEW_CREDENTIALS',
    'OTA_PASS',
    'TEST_CREDS',
    'PWR_UP',
    'SLEEP_EXP',
    'SW_RESET',
    'LOW_PWR',
    'UNK_LINK',
    'LOW_BATT',
    'OTA_FAILURE',
    'DIGITAL_MEAS',
    'RESET_PIN',
    'SIG_25',
    'SIG_26',
    'SIG_27',
    'SIG_28',
    'BUTTON_BUF',
    'HTTP_ERROR',
    'HARD_FAULT',
];

export const decodeSig = (sig: number) => {
    // mask to 32-bit integer
    sig = sig & 0xffffffff;

    return SIG_VALUES.map((val, i) => ((sig & (1 << i)) > 0 ? val : null))
        .filter((x) => x !== null)
        .join('|');
};

/** Returns whether or not a periodic usage type is included in the inputted consumptionTypes based on the selected fuelType. */
export const includesPeriodicUsage = (
    fuelType: DeviceFuelType,
    consumptionTypes: string[]
): boolean => {
    if (fuelType === 'oil') {
        for (const consumptionType of consumptionTypes) {
            if (periodicUsageTypesForOil.includes(consumptionType)) {
                return true;
            }
        }
    }

    if (fuelType === 'propane') {
        for (const consumptionType of consumptionTypes) {
            if (periodicUsageTypesForPropane.includes(consumptionType)) {
                return true;
            }
        }
    }
    return false;
};

export const isDeviceGen5 = (itemNumber: string) => {
    const generation = itemNumber.charAt(2);
    return generation === '5';
};

/**
 * Uses provided address with google geocoder to construct geometry information for firebase
 * @param address address of the device
 * @returns formatted geocode/geometry object
 */
export const getGeocodeData = async (
    address: string
): Promise<Geometry & { error?: string }> => {
    const geocoder = new window.google.maps.Geocoder();
    let geometry = {};
    await geocoder
        .geocode({ address }, (result, status) => {
            const geoResult = result?.[0];
            if (status === 'OK' && geoResult) {
                const address_components = geoResult.address_components;

                const zipCode = _.find(address_components, (component) => {
                    return _.includes(component.types, 'postal_code');
                })?.long_name;

                geometry = {
                    address: geoResult.formatted_address,
                    address_components: geoResult.address_components,
                    geometry: {
                        location_type: geoResult.geometry.location_type,
                        location: {
                            lat: geoResult.geometry.location.lat(),
                            lng: geoResult.geometry.location.lng(),
                            zipcode: zipCode ?? '',
                        },
                    },
                };
            }
        })
        .catch((error) => {
            if (error) {
                const errorParts = error.message?.split(':');
                const errorMessage =
                    geocodeErrorMessages[error.code] ??
                    errorParts[errorParts.length - 1];
                geometry = { error: errorMessage };
            }
        });

    return geometry as Geometry;
};
